
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { FeedbackReaction } from "@/types/feedback";
import { toast } from "sonner";
import { useEffect } from "react";

export const useFeedbackReactions = () => {
  const queryClient = useQueryClient();
  
  // Query to fetch all reactions
  const { data: reactions, isLoading } = useQuery({
    queryKey: ['feedback-reactions'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('feedback_reactions')
        .select('*');
      
      if (error) throw error;
      return data as FeedbackReaction[];
    },
  });
  
  // Set up realtime subscription
  useEffect(() => {
    const channel = supabase
      .channel('reactions-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'feedback_reactions' }, 
        () => {
          queryClient.invalidateQueries({ queryKey: ['feedback-reactions'] });
        }
      )
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);
  
  // Create reaction mutation
  const addReaction = useMutation({
    mutationFn: async ({ message_id, user_id, username }: { message_id: string, user_id: string, username: string }) => {
      // Check if the user has already reacted
      const existingReaction = reactions?.find(r => r.message_id === message_id && r.user_id === user_id);
      if (existingReaction) {
        throw new Error("Je hebt dit bericht al gemarkeerd");
      }

      const { data, error } = await (supabase as any)
        .from('feedback_reactions')
        .insert({
          message_id,
          user_id,
          username,
          reaction_type: 'star',
        })
        .select()
        .single();
      
      if (error) {
        console.error("Supabase reaction error:", error);
        throw error;
      }
      return data as FeedbackReaction;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback-reactions'] });
    },
    onError: (error: any) => {
      console.error('Error adding reaction:', error);
      toast.error(`Fout bij het toevoegen van reactie: ${error.message || "Onbekende fout"}`);
    }
  });
  
  // Delete reaction mutation
  const removeReaction = useMutation({
    mutationFn: async ({ message_id, user_id }: { message_id: string, user_id: string }) => {
      const { error } = await (supabase as any)
        .from('feedback_reactions')
        .delete()
        .match({ message_id, user_id });
      
      if (error) {
        console.error("Supabase remove reaction error:", error);
        throw error;
      }
      return { message_id, user_id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback-reactions'] });
    },
    onError: (error: any) => {
      console.error('Error removing reaction:', error);
      toast.error(`Fout bij het verwijderen van reactie: ${error.message || "Onbekende fout"}`);
    }
  });
  
  // Helper to check if a user has reacted to a message
  const hasUserReacted = (message_id: string, user_id?: string) => {
    if (!user_id || !reactions) return false;
    return reactions.some(r => r.message_id === message_id && r.user_id === user_id);
  };
  
  // Helper to count reactions for a message
  const getReactionCount = (message_id: string) => {
    if (!reactions) return 0;
    return reactions.filter(r => r.message_id === message_id).length;
  };
  
  return {
    reactions,
    isLoading,
    addReaction,
    removeReaction,
    hasUserReacted,
    getReactionCount
  };
};
